import { HttpErrorResponse } from '@angular/common/http';
import { IDictionary } from './api/modules/core/components/abstract/IDictionary';
import { endpointFailureOf } from './app-error';

/**
 * The messages the app actually shows, each resolved to a string.
 *
 * Deliberately narrower than {@link IDictionary}. The server has always sent the other entries and
 * nothing has ever read one, so carrying defaults for them would be inventing an owner for copy
 * that has none. A message earns a place here when something displays it.
 */
export type ResolvedDictionary = Required<Pick<IDictionary, 'unexpectedError' | 'updateAvailable' | 'connectionFailed'>>;

/**
 * The English copy the app falls back to.
 *
 * These exist because the dictionary arrives with the initialize response, and the failures that
 * most need wording are the ones where that response never came. They are also why the server can
 * adopt these messages whenever it likes: until it sends one, the wording here is already correct,
 * just not translated.
 */
export const DefaultDictionary: ResolvedDictionary = {
  unexpectedError: 'Something went wrong. Please refresh the page to try again.',
  updateAvailable: 'A new version is available. Please refresh the page to continue.',
  connectionFailed: "We couldn't reach the server. Please check your connection and refresh the page.",
};

/**
 * Lays the server's dictionary over the defaults.
 *
 * A key the server omits keeps its default, and so does a key sent empty: a blank string is the
 * shape an unfilled translation arrives in, and showing nothing at all is worse than showing
 * English. Anything else the server sends wins outright.
 *
 * @param patch - the dictionary from the initialize response, if one arrived.
 * @returns a dictionary with every message populated.
 */
export const resolveDictionary = (patch?: Partial<IDictionary>): ResolvedDictionary => {
  const resolved = { ...DefaultDictionary };
  if (!patch) {
    return resolved;
  }

  (Object.keys(resolved) as (keyof ResolvedDictionary)[]).forEach((key) => {
    const value = patch[key];
    if (typeof value === 'string' && value.trim() !== '') {
      resolved[key] = value;
    }
  });

  return resolved;
};

/**
 * Error names and message fragments that mean the running client is out of date.
 *
 * A release replaces the chunks on the CDN, so a page opened before it went out asks for files
 * that no longer exist. Browsers word this several ways, and the router wraps it in a name of our
 * own, so both are matched.
 */
const StaleClientErrorNames = ['ChunkLoadError', 'LazyLoadError'];
const StaleClientMessageFragments = ['dynamically imported module', 'Importing a module script', 'Loading chunk'];

/**
 * Decides whether a failure means the player is running a client the server has already replaced.
 */
const isStaleClientFailure = (error: unknown): boolean => {
  if (!(error instanceof Error)) {
    return false;
  }
  if (StaleClientErrorNames.includes(error.name)) {
    return true;
  }
  return StaleClientMessageFragments.some((fragment) => error.message?.includes(fragment));
};

/**
 * Chooses the wording to put in front of the player for a failure the server has not worded itself.
 *
 * Three outcomes, because three are all the player can act on: refresh because the client is out
 * of date, check the connection, or refresh and hope. Anything finer would be describing our
 * problem rather than theirs.
 *
 * @param error - the error that ended the experience.
 * @param dictionary - the resolved dictionary.
 * @returns the message to display.
 */
export const selectFailureCopy = (error: unknown, dictionary: ResolvedDictionary): string => {
  if (isStaleClientFailure(error)) {
    return dictionary.updateAvailable;
  }

  const failure = endpointFailureOf(error);
  if (error instanceof HttpErrorResponse || failure === 'transport' || failure === 'upload') {
    return dictionary.connectionFailed;
  }

  return dictionary.unexpectedError;
};
